import { EventsCollection } from '@models/EventsCollection'
import { eventsCollectionSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import {
  createEventsCollection as createEventsCollectionAPI,
  deleteEventsCollection as deleteEventsCollectionAPI,
  fetchEventsCollectionById,
  fetchEventsCollections,
  updateEventsCollection as updateEventsCollectionAPI,
} from '../../services/membership/EventsCollectionService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<EventsCollection> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'eventsCollection',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getEventsCollectionsSuccess,
  setQuery: setEventsCollectionQuery,
  reset: resetEventsCollectionState,
  resetQueryAndIds: resetEventsCollectionQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: EventsCollection }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectEventsCollections = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedEventsCollection = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectEventsCollectionById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectEventsCollectionsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedEventsCollection =
  (eventsCollection: EventsCollection | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<EventsCollection>(eventsCollection)
      )
    )

export const getEventsCollections = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchEventsCollections(params),
    [eventsCollectionSchema],
    slice.actions
  )

export const createEventsCollection = (eventsCollection: EventsCollection) =>
  SBAPI.CreateDispatched(
    createEventsCollectionAPI(eventsCollection),
    eventsCollectionSchema,
    slice.actions
  )

export const getEventsCollectionWithId = (id: number) =>
  SBAPI.FetchDispatched<EventsCollection>(
    fetchEventsCollectionById(id),
    eventsCollectionSchema,
    slice.actions
  )

export const updateEventsCollection = (eventsCollection: EventsCollection) =>
  SBAPI.UpdateDispatched<EventsCollection>(
    updateEventsCollectionAPI(eventsCollection),
    eventsCollection,
    slice.actions
  )

export const deleteEventsCollection = (eventsCollection: EventsCollection) =>
  SBAPI.DestroyDispatched<EventsCollection>(
    deleteEventsCollectionAPI(eventsCollection),
    eventsCollection,
    slice.actions
  )
